import { useState } from 'react'
import { Tabs } from '@components/ShoeSizeTable/ShoeSizeTable.styles'
import { SizeGuideTabButtonText } from '@components/SizeGuideTabButtonText/SizeGuideTabButtonText'
import type { AccessoriesSizeTableProps } from '@velasca/types'
import { SansText } from '@velasca/ui/sans-text'
import {
  AccessoryRow,
  AccessorySize,
  AccessoryTab,
  AccessoryTabContent,
  AccessoryTable,
  TableWrap,
} from './AccessoriesSizeTable.styles'
import { formatRow } from './AccessoriesSizeTable.utils'

export const AccessoriesSizeTable = ({
  sizeGuideId,
  table,
  convertible,
  firstButtonText,
  secondButtonText,
  localizedSizesLabel,
  localizedMeasure,
  defaultMeasurementSystem,
}: AccessoriesSizeTableProps) => {
  const [measurementSystem, setMeasurementSystem] = useState(
    defaultMeasurementSystem,
  )

  if (!sizeGuideId) {
    return null
  }

  if (!table) {
    return null
  }

  const isBelts = sizeGuideId.includes('cinture')

  const { [defaultMeasurementSystem]: headers, sizes: metricSizes } = table

  const imperialSizes = metricSizes?.map((row) =>
    row.map((size) => {
      if (typeof size === 'string') {
        return size
      }

      return Math.round((size / 2.54) * 10) / 10
    }),
  )

  const sizes =
    convertible && measurementSystem === 'imperial'
      ? imperialSizes
      : metricSizes
  const lastRow = formatRow(sizes[sizes.length - 1])
  const lastRowIndexes = lastRow.map(({ lastIndex }) => lastIndex)

  const hasSeparator = (index: number, row: (string | number)[]) =>
    !isBelts &&
    index !== 0 &&
    index !== row.length - 1 &&
    lastRowIndexes.includes(index)

  const localizeCell = (cell: string | number) => {
    switch (cell) {
      case 'localizedSizesLabel': {
        return localizedSizesLabel
      }
      case 'localizedMeasure': {
        return localizedMeasure[measurementSystem]
      }
      default: {
        return String(cell)
      }
    }
  }

  return (
    <TableWrap fullWidth={!sizeGuideId.includes('cinture')}>
      {convertible && (
        <Tabs>
          <AccessoryTab
            isSelected={measurementSystem === 'imperial'}
            order={defaultMeasurementSystem === 'metric' ? 0 : 1}
            onClick={() => {
              setMeasurementSystem('metric')
            }}
          >
            <SizeGuideTabButtonText>{firstButtonText}</SizeGuideTabButtonText>
          </AccessoryTab>
          <AccessoryTab
            isSelected={measurementSystem === 'metric'}
            order={defaultMeasurementSystem === 'imperial' ? 0 : 1}
            onClick={() => {
              setMeasurementSystem('imperial')
            }}
          >
            <SizeGuideTabButtonText>{secondButtonText}</SizeGuideTabButtonText>
          </AccessoryTab>
        </Tabs>
      )}

      <AccessoryTabContent convertible={convertible}>
        <AccessoryTable mobileRotate={!isBelts}>
          {/* Prints header rows */}
          {headers?.map((header, i) => (
            <AccessoryRow
              key={`header-${i}`}
              nColumn={header.length}
              convertible={convertible}
              mobileRotate={!isBelts}
            >
              {header.map((unitMeasureCell, j) => (
                <AccessorySize key={`${unitMeasureCell}-${i}-${j}`}>
                  <SansText
                    as="span"
                    fontWeight="500"
                    color={i === 0 ? 'primary' : 'dark'}
                  >
                    {localizeCell(unitMeasureCell)}
                  </SansText>
                </AccessorySize>
              ))}
            </AccessoryRow>
          ))}

          {/* Prints body rows */}
          {sizes.map((row, i) => {
            return (
              <AccessoryRow
                key={`size-${i}`}
                nColumn={row.length}
                convertible={convertible}
                mobileRotate={!isBelts}
              >
                {!isBelts && i === sizes.length - 1
                  ? lastRow.map(({ size, count, firstIndex }, j) => (
                      <AccessorySize
                        key={`${size}-${i}-${j}`}
                        separator={j !== 0 && j !== lastRow.length - 1}
                        start={firstIndex + 1}
                        end={firstIndex + count + 1}
                      >
                        <SansText as="span" fontWeight="500" color="primary">
                          {localizeCell(size)}
                        </SansText>
                      </AccessorySize>
                    ))
                  : row.map((cell, j) => (
                      <AccessorySize
                        key={`${cell}-${i}-${j}`}
                        highlight={!isBelts && i === 0}
                        separator={hasSeparator(j, row)}
                      >
                        <SansText
                          as="span"
                          fontWeight={i === 0 ? '500' : '400'}
                          color={!isBelts && i === 0 ? 'primary' : 'dark'}
                        >
                          {localizeCell(cell)}
                        </SansText>
                      </AccessorySize>
                    ))}
              </AccessoryRow>
            )
          })}
        </AccessoryTable>
      </AccessoryTabContent>
    </TableWrap>
  )
}
